import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { Analytics } from "../Analytics";
import LanguageOptions from "../components/LanguageOptions";
import LoadingScaffold from "../components/LoadingScaffold";
import PromptBottomSheet, {
  PromptBottomSheetOption,
} from "../components/PromptBottomSheet/PromptBottomSheet";
import SettingsScreen from "../components/SettingsScreen";
import useMixin from "../hooks/useMixin";
import { isLoading } from "../Loading";
import { getLocalStorageItem, setLocalStorageItem } from "../storage_utils";
import { STRINGS } from "../strings";
import { AppState } from "../system/AppState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { availableLangs, translate } from "../translate";
import { nullToUndefined } from "../utils/nullToUndefined";

interface SettingsActivityProps {
  appState: AppState;
  onClickNotifications: () => void;
  onClickProfile: () => void;
  onClickSocial: () => void;
  onClickReferral: () => void;
  onClickGeneral: () => void;
  onClickAgencies: () => void;
  onClickBankInformation: () => void;
  onClickSignOut: () => void;
  onClickGoBack: () => void;
  onClickInformationCenter: (caregiver: AppCaregiver) => void;
}

const SettingsActivity = (props: SettingsActivityProps) => {
  const [currentLanguage, setCurrentLanguage] = React.useState<string | undefined>(undefined);
  const [isSignOutPromptOpen, setSignOutPromptOpen] = React.useState(false);
  const [isLanguageOptionsOpen, setLanguageOptionsOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const mixin = useMixin(props.appState);

  const handleOpenLanguageOptions = React.useCallback(() => setLanguageOptionsOpen(true), []);
  const handleCloseLanguageOptions = React.useCallback(() => setLanguageOptionsOpen(false), []);

  const handleClickSignOut = React.useCallback(() => {
    Analytics.track("Sign Out Clicked");
    setSignOutPromptOpen(true);
  }, []);

  const handleChooseLanguage = React.useCallback((language: string) => {
    setLocalStorageItem("medflyt_language_override", language).then(() => location.reload());
  }, []);

  const handleCloseSignOutPrompt = React.useCallback(() => setSignOutPromptOpen(false), []);

  const handleChooseSignOutPrompt = React.useCallback(
    (value: boolean) => {
      if (value) {
        Analytics.track("Sign Out Activated");
        queryClient.clear();
        props.onClickSignOut.call(null);
      }
      handleCloseSignOutPrompt();
    },
    [handleCloseSignOutPrompt, props.onClickSignOut, queryClient]
  );

  React.useEffect(() => {
    getLocalStorageItem("medflyt_language_override").then((language) =>
      setCurrentLanguage(nullToUndefined(language))
    );
  }, []);

  if (isLoading(mixin.appCaregiverState)) {
    return (
      <LoadingScaffold title={translate(STRINGS.LOADING)} onClickGoBack={props.onClickGoBack} />
    );
  }

  return (
    <>
      <SettingsScreen
        caregiver={mixin.appCaregiverState.caregiver}
        currentLanguage={currentLanguage}
        onClickLanguage={handleOpenLanguageOptions}
        onClickGoBack={props.onClickGoBack}
        onClickProfile={props.onClickProfile}
        onClickSocial={props.onClickSocial}
        onClickReferral={props.onClickReferral}
        onClickGeneral={props.onClickGeneral}
        onClickAgencies={props.onClickAgencies}
        onClickBankInformation={props.onClickBankInformation}
        onClickSignOut={handleClickSignOut}
        onClickNotifications={props.onClickNotifications}
        onClickInformationCenter={props.onClickInformationCenter}
      />

      <LanguageOptions
        isOpen={isLanguageOptionsOpen}
        languages={availableLangs}
        onChoose={handleChooseLanguage}
        onCancel={handleCloseLanguageOptions}
      />

      <PromptBottomSheet
        isOpen={isSignOutPromptOpen}
        title={translate(STRINGS.SIGN_OUT) + "?"}
        message={translate(STRINGS.ARE_YOU_SURE_YOU_WANT_TO_SIGN_OUT)}
        options={signOutPromptOptions}
        onChoose={handleChooseSignOutPrompt}
        onRequestClose={handleCloseSignOutPrompt}
      />
    </>
  );
};

const signOutPromptOptions: PromptBottomSheetOption[] = [
  { value: false, label: translate(STRINGS.CANCEL) },
  { value: true, label: translate(STRINGS.SIGN_OUT) },
];

export default React.memo(SettingsActivity);
